import { isEmpty } from 'lodash-es';
import {
  forwardRef,
  type PropsWithChildren,
  useImperativeHandle,
  useRef,
} from 'react';

import { AnnotationsDisplay } from './components/AnnotationsDisplay.js';
import { AnnotationsAxis } from './components/axis/AnnotationsAxis.js';
import { ANNOTATIONS_CHART_ARIA_LABEL } from './constants.js';
import { useAnnotationsChartConfig } from './hooks/useAnnotationsChartConfig.js';
import { AnnotationsChartConfigProvider } from './providers/annotations-chart-config.provider.js';
import { Marker } from './slots/Marker.js';
import { Tooltip } from './slots/Tooltip.js';
import { Track } from './slots/Track.js';
import { XAxis } from './slots/XAxis.js';
import type { AnnotationsChartProps } from './types/annotations-chart-props.js';
import { AnnotationsChartRef } from './types/annotations-chart-ref.js';
import { iterateSlots } from './utils/iterate-slots.js';
import { buildScale } from './utils/scales.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { defaultChartSize } from '../../core/styles/defaultChartValues.sty.js';
import { AnnotationsScrollWrapper } from '../core/components/annotations/components/overlays/AnnotationsScrollWrapper.js';
import {
  DEFAULT_LABELS_OFFSET,
  TRACK_HEIGHT,
  TRACK_SEPARATOR_STROKE_WIDTH,
} from '../core/components/annotations/constants.js';
import { isNumericalData } from '../core/components/annotations/utils/is-numerical-marker.js';
import { ChartStateHandler } from '../core/components/chart-states/ChartStateHandler.js';
import { getChartStatesTemplates } from '../core/components/chart-states/utils/get-chart-states-template.js';
import { DataDtrumContext } from '../core/context/DataDtrumContext.js';
import { useObserveContainerSize } from '../core/hooks/useObserveContainerSize.js';
import { EmptyStateSlot } from '../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../core/slots/chart-states/ErrorStateSlot.js';
import { ChartType } from '../core/types/chart-types.js';
import { coerceSizeValue } from '../core/utils/coerceSizeValue.js';

const AXIS_HEIGHT = 25;
const X_AXIS_LABEL_HEIGHT = 10;

const calculateXAxisHeight = (isHidden: boolean, label?: string) =>
  isHidden ? 0 : label ? AXIS_HEIGHT + X_AXIS_LABEL_HEIGHT : AXIS_HEIGHT;

export const _AnnotationsChart = forwardRef<
  AnnotationsChartRef,
  PropsWithChildren<AnnotationsChartProps>
>((props, forwardedRef) => {
  const {
    annotationsActions,
    children,
    loading = false,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
  } = props;

  const [containerDimension, sizeObserverRef] = useObserveContainerSize();

  const { aggregatedMarkers, tracks, xAxisProps, tooltipProps } =
    iterateSlots(children);
  const { emptyState, errorState } = getChartStatesTemplates(children);

  const hasNumericalData =
    aggregatedMarkers.length > 0 && isNumericalData(aggregatedMarkers);

  const config = useAnnotationsChartConfig(
    props,
    xAxisProps,
    tooltipProps,
    hasNumericalData,
  );

  const { showLabels, xAxisConfig, tooltipConfig, textOverflowConfig } = config;

  const shouldShowLabels = tracks.some(({ label }) => label) && showLabels;

  const labelsOffset = shouldShowLabels
    ? DEFAULT_LABELS_OFFSET + config.labelWidth
    : 0;

  const scale = buildScale(
    aggregatedMarkers,
    containerDimension.width - labelsOffset,
    xAxisConfig,
  );

  const shownTracks = tracks.filter(({ hidden }) => !hidden);

  const tracksHeight =
    shownTracks.length * TRACK_HEIGHT + TRACK_SEPARATOR_STROKE_WIDTH;

  const axisHeight = calculateXAxisHeight(
    xAxisConfig.hidden,
    config.xAxisConfig.label,
  );
  const coercedHeight = coerceSizeValue(config.height);

  const height = coercedHeight ?? tracksHeight + axisHeight;

  const scrollWrapperHeight = Math.min(
    tracksHeight,
    Math.max(0, containerDimension.height - axisHeight),
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const rootRef = useMergeRefs<AnnotationsChartRef | HTMLDivElement>([
    forwardedRef,
    containerRef,
    sizeObserverRef,
  ]);

  useImperativeHandle(forwardedRef, () => ({
    element: containerRef.current,
    getConfig: () => {
      return JSON.stringify({
        type: ChartType.AnnotationsChart,
        ...config,
      });
    },
  }));

  return (
    <ChartStateHandler
      empty={isEmpty(tracks)}
      height={'100%'}
      emptyState={emptyState}
      errorState={errorState}
      isLoading={loading}
      className={defaultChartSize}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
    >
      <div
        style={{ height }}
        ref={rootRef}
        aria-label={ANNOTATIONS_CHART_ARIA_LABEL}
        role="img"
      >
        <AnnotationsChartConfigProvider config={config}>
          <DataDtrumContext.Provider value={{ dataDtrumAllow, dataDtrumMask }}>
            <AnnotationsScrollWrapper
              height={scrollWrapperHeight}
              width={containerDimension.width}
            >
              <AnnotationsDisplay
                tracks={tracks}
                scale={scale}
                annotationsActions={annotationsActions}
                showLabels={shouldShowLabels}
                labelsOffset={labelsOffset}
                width={containerDimension.width}
                tooltipConfig={tooltipConfig}
                textOverflowOptions={textOverflowConfig}
              />
            </AnnotationsScrollWrapper>
          </DataDtrumContext.Provider>
          <AnnotationsAxis
            height={axisHeight}
            scale={scale}
            width={containerDimension.width}
            labelsOffset={labelsOffset}
          />
        </AnnotationsChartConfigProvider>
      </div>
    </ChartStateHandler>
  );
});

/**
 * The `AnnotationsChart` is a visualization tool designed to highlight significant events or data points
 * on a timeline or other graph types. It allows the user to annotate specific moments with labels or markers,
 * providing additional context or insights directly within the chart. This chart can be used standalone
 * or integrated with other chart types, such `HistogramChart` or  `TimeseriesChart`, to enrich the data presentation
 * by drawing attention to each event.
 * @public
 */
export const AnnotationsChart = Object.assign(_AnnotationsChart, {
  Track,
  Marker,
  XAxis,
  Tooltip,
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
});
